import { useState, useRef } from 'react'
import { apiClient } from '@/services/api'
import { useErrorHandler } from '@/hooks/useAuth'
import { CellEnum } from '@/enums/cell.enum'
import { ensureString, ensureNumber } from '@/utils/type.util'
import { ExcelHeader } from '@/utils/tool-management/excel-header.constant'
import {
  buildImageLookup,
  extractTrolleyDescription,
  getDataRowsWithoutDescription,
  rowsToDrawerMap,
  type UploadedImageMeta,
  type ToolRow,
} from '@/utils/tool-management/tool-management.util'
import { QRCodeRegenerationStatus } from '@/utils/qr-code/qr-code-regeneration.constant'

import { useBulkImportTrolleys } from './useBulkImportTrolleys'

type RawTrolleyRow = unknown[]
type RawTrolleySheet = RawTrolleyRow[]

interface ToolPayload {
  part_number: string
  chinese_name: string
  description: string
  quantity: number
  temp_image_key: Nullable<string>
}

interface DrawerPayload {
  name: string
  tools: ToolPayload[]
}

interface TrolleyPayload {
  name: string
  description?: Nullable<string>
  drawers: DrawerPayload[]
}

interface BulkImportPayload {
  trolleys: TrolleyPayload[]
  session_id: string
}

interface CreateTrolleyDefaults {
  drawerDescription?: string
}

interface CreateTrolleyParams {
  trolleysData: Nillable<Record<string, RawTrolleySheet>>
  images?: UploadedImageMeta[]
  sessionId?: Nullable<string>
  defaults?: CreateTrolleyDefaults
}

interface UseCreateTrolleyState {
  isLoading: boolean
  isSuccess: boolean
  error: Nullable<string>
}

interface BulkImportResponse {
  process_id: string
  status: string
  message: string
}

interface UseCreateTrolleyReturn extends UseCreateTrolleyState {
  createTrolleys: (
    params: CreateTrolleyParams,
    onDone?: () => Promise<void>
  ) => Promise<unknown>
  reset: () => void
}

export const buildBulkImportPayload = (
  params: CreateTrolleyParams
): BulkImportPayload => {
  const { trolleysData, images = [], sessionId } = params

  if (!trolleysData) {
    return {
      trolleys: [],
      session_id: Math.random().toString(36).slice(2),
    }
  }

  const imageLookup = buildImageLookup(images)

  const isHeaderRow = (row: RawTrolleyRow): boolean => {
    const drawer = ensureString(row[CellEnum.Drawer]).toLowerCase()
    const pn = ensureString(row[CellEnum.Pn]).toLowerCase()
    const quantity = ensureString(row[CellEnum.Quantity]).toLowerCase()
    return (
      drawer === ExcelHeader.Drawer &&
      (pn === ExcelHeader.Pn || pn === ExcelHeader.PnAlt) &&
      quantity === ExcelHeader.Quantity
    )
  }

  const getDataRows = (sheet: RawTrolleySheet): RawTrolleySheet => {
    if (!sheet.length) return []
    const dataWithoutDescription = getDataRowsWithoutDescription(
      sheet as unknown as ToolRow[]
    )
    if (isHeaderRow(dataWithoutDescription[0]))
      return dataWithoutDescription.slice(1)
    return dataWithoutDescription
  }

  const isValidQuantity = (value: unknown): boolean => {
    if (value === null || value === undefined || value === '') return false
    const parsed = Number(value)
    return Number.isFinite(parsed)
  }

  const trolleys: TrolleyPayload[] = Object.entries(trolleysData)
    .map(([trolleyName, sheet]): Nullable<TrolleyPayload> => {
      const sheetData = Array.isArray(sheet)
        ? (sheet as unknown as ToolRow[])
        : []
      const trolleyDescription = extractTrolleyDescription(sheetData)
      const sheetRows: RawTrolleySheet = Array.isArray(sheet)
        ? getDataRows(sheet)
        : []
      const drawersByName = rowsToDrawerMap(sheetRows)

      const drawers: DrawerPayload[] = Object.entries(drawersByName).reduce<
        DrawerPayload[]
      >((acc, [drawerName, drawerRows]) => {
        const tools: ToolPayload[] = drawerRows
          .map(row => {
            if (!isValidQuantity(row[CellEnum.Quantity])) return null

            const quantity = ensureNumber(row[CellEnum.Quantity])
            const imageLabel = ensureString(row[CellEnum.Image])

            let imageMeta: UploadedImageMeta | undefined = undefined
            if (imageLabel) {
              imageMeta = imageLookup[imageLabel]

              if (!imageMeta) {
                const lowerKey = imageLabel.toLowerCase().trim()
                imageMeta = imageLookup[lowerKey]
              }

              if (!imageMeta) {
                const labelWithoutExt = imageLabel
                  .replace(/\.[^/.]+$/, '')
                  .toLowerCase()
                  .trim()
                const foundImage = Object.values(imageLookup).find(
                  img =>
                    img.name
                      .replace(/\.[^/.]+$/, '')
                      .toLowerCase()
                      .trim() === labelWithoutExt
                )
                if (foundImage) {
                  imageMeta = foundImage
                }
              }
            }

            const part_number = ensureString(row[CellEnum.Pn])
            const chinese_name = ensureString(row[CellEnum.ChineseName])
            const description = ensureString(row[CellEnum.Description])

            if (!part_number || !chinese_name) return null
            if (description.length > 100) return null

            const tool: ToolPayload = {
              part_number,
              chinese_name,
              description,
              quantity,
              temp_image_key: imageMeta?.fileKey ?? null,
            }

            return tool
          })
          .filter((tool): tool is ToolPayload => Boolean(tool))

        if (tools.length) {
          acc.push({
            name: drawerName,
            tools,
          })
        }

        return acc
      }, [])

      return drawers.length
        ? ({
            name: trolleyName,
            ...(trolleyDescription && { description: trolleyDescription }),
            drawers,
          } as TrolleyPayload)
        : null
    })
    .filter(
      (trolley): trolley is TrolleyPayload =>
        trolley !== null && trolley !== undefined
    )

  return {
    trolleys,
    session_id: sessionId ?? Math.random().toString(36).slice(2),
  }
}

export function useCreateTrolley(): UseCreateTrolleyReturn {
  const { handleError } = useErrorHandler()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [error, setError] = useState<Nullable<string>>(null)
  const onDoneRef = useRef<(() => Promise<void>) | undefined>(undefined)

  const { startPollingByProcessId, isLoading: isPolling } =
    useBulkImportTrolleys({
      onDone: async () => {
        setIsSuccess(true)
        setError(null)
        setIsLoading(false)
        if (onDoneRef.current) {
          await onDoneRef.current()
          onDoneRef.current = undefined
        }
      },
    })

  const createTrolleys = async (
    params: CreateTrolleyParams,
    onDone?: () => Promise<void>
  ) => {
    setIsLoading(true)
    setIsSuccess(false)
    setError(null)
    onDoneRef.current = onDone

    try {
      const payload = buildBulkImportPayload(params)
      const response = await apiClient.post<BulkImportResponse>(
        '/trolleys/bulk-import',
        payload
      )
      const { process_id, status } = response.data
      const normalizedStatus = status.toLowerCase()

      if (
        normalizedStatus === QRCodeRegenerationStatus.IN_PROGRESS &&
        process_id
      ) {
        startPollingByProcessId(process_id)
      } else if (normalizedStatus === QRCodeRegenerationStatus.DONE) {
        setIsSuccess(true)
        setError(null)
        setIsLoading(false)
        if (onDone) {
          await onDone()
        }
        onDoneRef.current = undefined
      } else {
        setIsLoading(false)
        onDoneRef.current = undefined
      }

      return response.data
    } catch (error) {
      const errorMessage = handleError(error)
      setError(errorMessage)
      setIsSuccess(false)
      setIsLoading(false)
      onDoneRef.current = undefined
      throw error
    }
  }

  const reset = () => {
    setIsLoading(false)
    setIsSuccess(false)
    setError(null)
    onDoneRef.current = undefined
  }

  return {
    isLoading: isLoading || isPolling,
    isSuccess,
    error,
    createTrolleys,
    reset,
  }
}
